import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiAlertCircle,
  FiCalendar,
  FiCheck,
  FiClock,
  FiFile,
  FiLock,
  FiMessageSquare,
  FiPaperclip,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";

import API from "../../../../../utils/apiCalling";
import { config } from "../../../../../utils/apiUrl";
import useSwal from "../../../../../hooks/useSwal";

import "./AddTaskComment.css";

const COMMENT_TYPES = [
  {
    value: "NOTE",
    label: "Normal Note",
    description:
      "General task note visible to the assigned employee.",
  },
  {
    value: "FOLLOW_UP",
    label: "Follow-up",
    description:
      "Schedule the next task follow-up date and time.",
  },
  {
    value: "CUSTOMER_UPDATE",
    label: "Customer Update",
    description:
      "Record a customer call, response, approval or discussion.",
  },
  {
    value: "WORK_LOG",
    label: "Work Log",
    description:
      "Record work completed, diagnosis, repair or time spent.",
  },
  {
    value: "INTERNAL_NOTE",
    label: "Internal Note",
    description:
      "Private note visible only to administrators.",
  },
];

const MAX_FILES = 10;
const MAX_FILE_SIZE = 20 * 1024 * 1024;

const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(
      size / 1024
    ).toFixed(1)} KB`;
  }

  return `${(
    size /
    (1024 * 1024)
  ).toFixed(1)} MB`;
};

const getLocalDateTime = (
  value
) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const offset =
    date.getTimezoneOffset() *
    60 *
    1000;

  return new Date(
    date.getTime() - offset
  )
    .toISOString()
    .slice(0, 16);
};

const getDefaultFollowUp = () => {
  const date = new Date();

  date.setDate(
    date.getDate() + 1
  );

  date.setHours(
    11,
    0,
    0,
    0
  );

  return getLocalDateTime(date);
};

const AddTaskComment = ({
  open,
  task,
  onClose,
  onSuccess,
}) => {
  const api = useMemo(
    () => new API(),
    []
  );

  const swal = useSwal();

  const fileInputRef =
    useRef(null);

  const [form, setForm] =
    useState({
      commentType: "NOTE",
      message: "",
      followUpAt: "",
      visibility: "ALL",
      changeTaskFollowUp: false,
      changeTaskStatus: false,
    });

  const [files, setFiles] =
    useState([]);

  const [errors, setErrors] =
    useState({});

  const [submitting, setSubmitting] =
    useState(false);

  const isFollowUp =
    form.commentType ===
    "FOLLOW_UP";

  const isInternalNote =
    form.commentType ===
    "INTERNAL_NOTE";

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm({
      commentType: "NOTE",
      message: "",
      followUpAt:
        task?.followUpAt
          ? getLocalDateTime(
              task.followUpAt
            )
          : "",
      visibility: "ALL",
      changeTaskFollowUp: false,
      changeTaskStatus: false,
    });

    setFiles([]);
    setErrors({});
    setSubmitting(false);
  }, [open, task]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const handleEscape = (
      event
    ) => {
      if (
        event.key ===
          "Escape" &&
        !submitting
      ) {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [
    open,
    submitting,
    onClose,
  ]);

  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
      form: "",
    }));
  };

  const handleCommentTypeChange = (
    event
  ) => {
    const commentType =
      event.target.value;

    setForm((previous) => ({
      ...previous,

      commentType,

      visibility:
        commentType ===
        "INTERNAL_NOTE"
          ? "ADMIN_ONLY"
          : "ALL",

      followUpAt:
        commentType ===
          "FOLLOW_UP"
          ? previous.followUpAt ||
            getDefaultFollowUp()
          : previous.followUpAt,

      changeTaskFollowUp:
        commentType ===
          "FOLLOW_UP",

      changeTaskStatus:
        commentType ===
          "FOLLOW_UP",
    }));

    setErrors({});
  };

  const addSelectedFiles = (
    selectedFiles
  ) => {
    const incomingFiles =
      Array.from(
        selectedFiles || []
      );

    if (
      incomingFiles.length === 0
    ) {
      return;
    }

    const existingKeys =
      new Set(
        files.map(
          (file) =>
            `${file.name}-${file.size}-${file.lastModified}`
        )
      );

    const acceptedFiles = [];
    const rejectedMessages = [];

    incomingFiles.forEach(
      (file) => {
        const key =
          `${file.name}-${file.size}-${file.lastModified}`;

        if (
          existingKeys.has(key)
        ) {
          return;
        }

        if (
          file.size >
          MAX_FILE_SIZE
        ) {
          rejectedMessages.push(
            `${file.name} exceeds 20 MB.`
          );

          return;
        }

        acceptedFiles.push(file);
        existingKeys.add(key);
      }
    );

    const availableSlots =
      MAX_FILES - files.length;

    const finalFiles =
      acceptedFiles.slice(
        0,
        availableSlots
      );

    if (
      acceptedFiles.length >
      availableSlots
    ) {
      rejectedMessages.push(
        `Maximum ${MAX_FILES} attachments are allowed.`
      );
    }

    setFiles((previous) => [
      ...previous,
      ...finalFiles,
    ]);

    if (
      rejectedMessages.length
    ) {
      setErrors((previous) => ({
        ...previous,
        files:
          rejectedMessages.join(
            " "
          ),
      }));
    } else {
      setErrors((previous) => ({
        ...previous,
        files: "",
      }));
    }

    if (
      fileInputRef.current
    ) {
      fileInputRef.current.value =
        "";
    }
  };

  const handleFileChange = (
    event
  ) => {
    addSelectedFiles(
      event.target.files
    );
  };

  const handleDrop = (
    event
  ) => {
    event.preventDefault();

    addSelectedFiles(
      event.dataTransfer.files
    );
  };

  const removeFile = (
    fileIndex
  ) => {
    setFiles((previous) =>
      previous.filter(
        (_, index) =>
          index !== fileIndex
      )
    );
  };

  const validate = () => {
    const nextErrors = {};

    if (
      !form.message.trim()
    ) {
      nextErrors.message =
        "Please enter a note.";
    }

    if (
      form.message.trim()
        .length > 5000
    ) {
      nextErrors.message =
        "Note cannot exceed 5000 characters.";
    }

    if (
      isFollowUp &&
      !form.followUpAt
    ) {
      nextErrors.followUpAt =
        "Follow-up date and time are required.";
    }

    if (
      form.followUpAt &&
      Number.isNaN(
        new Date(
          form.followUpAt
        ).getTime()
      )
    ) {
      nextErrors.followUpAt =
        "Invalid follow-up date.";
    }

    setErrors(nextErrors);

    return (
      Object.keys(nextErrors)
        .length === 0
    );
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    if (!task?._id) {
      setErrors({
        form:
          "Task information is missing.",
      });

      return;
    }

    try {
      setSubmitting(true);
      setErrors({});

      const formData =
        new FormData();

      formData.append(
        "commentType",
        form.commentType
      );

      formData.append(
        "message",
        form.message.trim()
      );

      formData.append(
        "visibility",
        isInternalNote
          ? "ADMIN_ONLY"
          : form.visibility
      );

      formData.append(
        "changeTaskFollowUp",
        String(
          form.changeTaskFollowUp
        )
      );

      formData.append(
        "changeTaskStatus",
        String(
          form.changeTaskStatus
        )
      );

      if (form.followUpAt) {
        formData.append(
          "followUpAt",
          new Date(
            form.followUpAt
          ).toISOString()
        );
      }

      files.forEach((file) => {
        formData.append(
          "attachments",
          file
        );
      });

      const response =
        await api.postFormData(
          config.addTaskComment(
            task._id
          ),
          formData
        );

      if (
        response?.status !==
        "success"
      ) {
        throw new Error(
          response?.message ||
            "Unable to add task note."
        );
      }

      await swal.success({
        icon: "success",
        title: isFollowUp
          ? "Follow-up Added"
          : "Note Added",

        text:
          response?.message ||
          (
            isFollowUp
              ? "Task follow-up added successfully."
              : "Task note added successfully."
          ),

        timer: 1400,
        showConfirmButton:
          false,
      });

      if (
        typeof onSuccess ===
        "function"
      ) {
        await onSuccess(
          response?.data ||
            null
        );
      }
    } catch (requestError) {
      console.error(
        "Add task comment error:",
        requestError
      );

      const message =
        requestError?.response
          ?.data?.message ||
        requestError?.data
          ?.message ||
        requestError?.message ||
        "Unable to add task note.";

      setErrors({
        form: message,
      });

      swal.error({
        icon: "error",
        title:
          "Unable to add note",
        text: message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !task) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="task-comment-root">
      <button
        type="button"
        className="task-comment-backdrop"
        onClick={() => {
          if (!submitting) {
            onClose?.();
          }
        }}
        aria-label="Close task note"
      />

      <section
        className="task-comment-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-comment-title"
      >
        <header className="task-comment-header">
          <div>
            <span className="task-comment-header-icon">
              <FiMessageSquare />
            </span>

            <div>
              <small>
                {task.taskCode}
              </small>

              <h2 id="task-comment-title">
                Add Task Note
              </h2>
            </div>
          </div>

          <button
            type="button"
            className="task-comment-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <FiX />
          </button>
        </header>

        <form
          className="task-comment-form"
          onSubmit={handleSubmit}
        >
          <div className="task-comment-body">
            {errors.form ? (
              <div className="task-comment-error-banner">
                <FiAlertCircle />

                <span>
                  {errors.form}
                </span>
              </div>
            ) : null}

            <section className="task-comment-task-card">
              <small>
                ADDING UPDATE TO
              </small>

              <strong>
                {task.title}
              </strong>

              <span>
                Assigned to{" "}
                {task?.assignedTo
                  ?.name ||
                  "employee"}
              </span>
            </section>

            <div className="task-comment-field">
              <label htmlFor="task-comment-type">
                Update Type
              </label>

              <select
                id="task-comment-type"
                name="commentType"
                value={
                  form.commentType
                }
                onChange={
                  handleCommentTypeChange
                }
                disabled={submitting}
              >
                {COMMENT_TYPES.map(
                  (option) => (
                    <option
                      key={
                        option.value
                      }
                      value={
                        option.value
                      }
                    >
                      {
                        option.label
                      }
                    </option>
                  )
                )}
              </select>

              <small className="task-comment-help">
                {
                  COMMENT_TYPES.find(
                    (item) =>
                      item.value ===
                      form.commentType
                  )?.description
                }
              </small>
            </div>

            {isInternalNote ? (
              <div className="task-comment-private-banner">
                <FiLock />

                <div>
                  <strong>
                    Private admin note
                  </strong>

                  <span>
                    This note will not be visible to the assigned employee.
                  </span>
                </div>
              </div>
            ) : null}

            <div className="task-comment-field">
              <label htmlFor="task-comment-message">
                Note *
              </label>

              <textarea
                id="task-comment-message"
                name="message"
                value={form.message}
                onChange={
                  handleChange
                }
                placeholder={
                  isFollowUp
                    ? "Enter follow-up instructions, customer response or next action..."
                    : "Enter task update, instruction or note..."
                }
                rows={6}
                maxLength={5000}
                disabled={submitting}
              />

              <div className="task-comment-field-footer">
                {errors.message ? (
                  <small className="task-comment-error">
                    {
                      errors.message
                    }
                  </small>
                ) : (
                  <span />
                )}

                <small>
                  {
                    form.message
                      .length
                  }
                  /5000
                </small>
              </div>
            </div>

            {isFollowUp ? (
              <section className="task-comment-follow-up-card">
                <div className="task-comment-follow-up-heading">
                  <FiCalendar />

                  <div>
                    <strong>
                      Follow-up Schedule
                    </strong>

                    <span>
                      Set the next action date and optionally move the task to Waiting Follow-up.
                    </span>
                  </div>
                </div>

                <div className="task-comment-field">
                  <label htmlFor="task-comment-follow-up">
                    Follow-up Date & Time *
                  </label>

                  <div className="task-comment-input-icon">
                    <FiClock />

                    <input
                      id="task-comment-follow-up"
                      type="datetime-local"
                      name="followUpAt"
                      value={
                        form.followUpAt
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        submitting
                      }
                    />
                  </div>

                  {errors.followUpAt ? (
                    <small className="task-comment-error">
                      {
                        errors.followUpAt
                      }
                    </small>
                  ) : null}
                </div>

                <label className="task-comment-check-row">
                  <input
                    type="checkbox"
                    name="changeTaskFollowUp"
                    checked={
                      form.changeTaskFollowUp
                    }
                    onChange={
                      handleChange
                    }
                    disabled={
                      submitting
                    }
                  />

                  <span>
                    <FiCheck />
                  </span>

                  <div>
                    <strong>
                      Update task follow-up date
                    </strong>

                    <small>
                      The selected date will become the task's active follow-up date.
                    </small>
                  </div>
                </label>

                <label className="task-comment-check-row">
                  <input
                    type="checkbox"
                    name="changeTaskStatus"
                    checked={
                      form.changeTaskStatus
                    }
                    onChange={
                      handleChange
                    }
                    disabled={
                      submitting
                    }
                  />

                  <span>
                    <FiCheck />
                  </span>

                  <div>
                    <strong>
                      Change status to Waiting Follow-up
                    </strong>

                    <small>
                      Keep this unchecked when work can continue before the follow-up.
                    </small>
                  </div>
                </label>
              </section>
            ) : null}

            <section className="task-comment-attachment-section">
              <div className="task-comment-section-title">
                <FiPaperclip />

                <div>
                  <strong>
                    Attachments
                  </strong>

                  <span>
                    Add images, PDFs or supporting files. Maximum 10 files, 20 MB each.
                  </span>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="task-comment-hidden-input"
                onChange={
                  handleFileChange
                }
                disabled={
                  submitting
                }
              />

              <button
                type="button"
                className="task-comment-upload-zone"
                onClick={() =>
                  fileInputRef.current?.click()
                }
                onDragOver={(
                  event
                ) =>
                  event.preventDefault()
                }
                onDrop={
                  handleDrop
                }
                disabled={
                  submitting ||
                  files.length >=
                    MAX_FILES
                }
              >
                <FiUploadCloud />

                <strong>
                  Choose files or drag them here
                </strong>

                <span>
                  {files.length}/
                  {MAX_FILES} selected
                </span>
              </button>

              {errors.files ? (
                <small className="task-comment-error">
                  {errors.files}
                </small>
              ) : null}

              {files.length > 0 ? (
                <div className="task-comment-file-list">
                  {files.map(
                    (
                      file,
                      index
                    ) => (
                      <article
                        key={`${file.name}-${file.lastModified}`}
                      >
                        <span className="task-comment-file-icon">
                          <FiFile />
                        </span>

                        <div>
                          <strong>
                            {
                              file.name
                            }
                          </strong>

                          <small>
                            {formatFileSize(
                              file.size
                            )}
                          </small>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            removeFile(
                              index
                            )
                          }
                          disabled={
                            submitting
                          }
                          title="Remove attachment"
                        >
                          <FiTrash2 />
                        </button>
                      </article>
                    )
                  )}
                </div>
              ) : null}
            </section>
          </div>

          <footer className="task-comment-footer">
            <button
              type="button"
              className="task-comment-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="task-comment-submit"
              disabled={submitting}
            >
              {submitting
                ? "Saving..."
                : isFollowUp
                  ? "Add Follow-up"
                  : "Add Note"}
            </button>
          </footer>
        </form>
      </section>
    </div>,
    document.body
  );
};

export default AddTaskComment;